import { AnimatePresence, motion } from "framer-motion";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FiChevronDown } from "react-icons/fi";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { toast } from "react-toastify";
import Button from "../../../../../components/shared/Button/Button";
import Spinner from "../../../../../components/shared/spinner/Spinner";
import { useId } from "../../../../../hooks/useId";
import coachService from "../../../../../services/coach";
import { twMerge } from "tailwind-merge";
import { useUserData } from "../../../../../hooks/useUserData";
import Divider from "../../../../../components/shared/Divider/Divider";
import Checkbox from "../../../../../components/shared/Checkbox";
import { isEnterpriseMaster } from "../../../../../common/envConfig";

export default function SelectGolfers({
  header,
  onOptionSelect,
  placeholder,
  values,
}) {
  const { userData } = useUserData();

  const [baseOptions, setBaseOptions] = useState([]);
  const [searchOptions, setSearchOptions] = useState(null);
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);

  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  const rootRef = useRef(null);
  const listRef = useRef(null);
  const id = useId();

  const selectedIds = useMemo(
    () => new Set(values.map((v) => v?.userId?.toLowerCase())),
    [values],
  );

  const fetchStudents = useCallback(
    async ({ search = "" }) => {
      try {
        if (userData?.role === "admin" && search) {
          const res = await coachService.searchByNameOrEmailV2({
            coachId: "",
            text: search,
          });

          return (res || []).map((el) => ({
            email: el?.email || "",
            userId: el?.id,
            userName: `${el?.firstName || ""} ${el?.lastName || ""}`.trim(),
          }));
        }

        if (
          isEnterpriseMaster(userData?.subscriptionType) &&
          userData?.enterpriseAccountId
        ) {
          const result = await coachService.enterpriseCoachesAndStudents({
            limit: 10,
            masterId: userData?.enterpriseAccountId,
            offset: 0,
            order: "desc",
            search,
            sort: "userCreated",
            subscriptionFilter: null,
          });

          return (result?.result || [])
            .filter((el) =>
              el?.invite == null ? true : el.invite.status !== "pending",
            )
            .map((el) => ({
              email: el?.email || "",
              userId: el?.uid,
              userName: `${el?.firstName || ""} ${el?.lastName || ""}`.trim(),
            }));
        }

        const result = await coachService.loadStudentsForCoach({
          coachId: userData.uid,
          filter: { firstNameOrLastNameOrEmail: search },
          isPendingInviteNeeded: true,
          limit: 10,
          offset: 0,
          order: "desc",
          sort: "userCreated",
          subscriptionLimit: 20,
        });

        return (result?.result || [])
          .filter((el) => el?.status !== "pending")
          .map((el) => ({
            email: el?.user?.email || "",
            userId: el?.user?.uid || "",
            userName: `${el?.user?.firstName || ""} ${
              el?.user?.lastName || ""
            }`.trim(),
          }));
      } catch (error) {
        console.error("fetchStudents error:", error);
        throw error;
      }
    },
    [userData],
  );

  useEffect(() => {
    if (!userData) return;
    (async () => {
      try {
        const list = await fetchStudents({ search: "" });
        setBaseOptions(list);
      } catch (error) {
        console.error("Error loading users:", error);
        toast.error(error?.message || "Failed to load users");
      }
    })();
  }, [userData, fetchStudents]);

  useEffect(() => {
    if (!open) return;
    if (!searchValue.trim()) {
      setSearchOptions(null);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      const rid = ++requestIdRef.current;
      setLoading(true);
      try {
        const result = await fetchStudents({ search: searchValue.trim() });
        if (rid === requestIdRef.current) setSearchOptions(result ?? []);
      } catch (error) {
        console.error("Search students error:", error);
        if (rid === requestIdRef.current) setSearchOptions([]);
        toast.error(error?.message || "Failed to search users");
      } finally {
        if (rid === requestIdRef.current) setLoading(false);
      }
    }, 350);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchValue, fetchStudents, open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const active = listRef.current.querySelector('[data-active="true"]');
    active?.scrollIntoView({ block: "nearest" });
  }, [open]);

  const { selectedList, unselectedList } = useMemo(() => {
    const base = searchOptions ?? baseOptions;
    if (!base?.length) return { selectedList: [], unselectedList: [] };

    const byId = new Map();
    for (const o of base) byId.set(o?.userId?.toLowerCase(), o);
    // ensure selected are present even if not in base (or when search filters them in)
    for (const s of values) byId.set(s?.userId?.toLowerCase(), s);

    const all = Array.from(byId.values());

    const selectedList = all.filter((o) =>
      selectedIds.has(o?.userId?.toLowerCase()),
    );
    const unselectedList = all.filter(
      (o) => !selectedIds.has(o?.userId?.toLowerCase()),
    );
    return { selectedList, unselectedList };
  }, [baseOptions, searchOptions, values, selectedIds]);

  const displayText = useMemo(() => {
    if (!values?.length) return placeholder;
    return `${values[0]?.userName}${
      values.length > 1 ? ` +${values.length - 1}` : ""
    }`;
  }, [values, placeholder]);

  const handlePick = (opt) => {
    onOptionSelect(opt);
  };

  const close = () => setOpen(false);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!rootRef.current?.contains(e.target)) close();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const renderOption = (el, isChecked) => (
    <div
      aria-selected={isChecked}
      className={twMerge(
        "flex w-full cursor-pointer items-center gap-2 rounded-sm p-2.5 text-left text-sm",
        "hover:bg-gray100 focus:bg-gray100 focus:outline-none",
      )}
      data-active={isChecked ? "true" : undefined}
      key={el.userId}
      onClick={() => handlePick(el)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handlePick(el);
        }
      }}
      role="option"
      tabIndex={0}
    >
      <Checkbox isChecked={isChecked} />

      <span className="flex min-w-0 flex-col">
        <span className="truncate">{el.userName}</span>
        <span className="text-gray500 truncate text-xs">{el.email}</span>
      </span>
    </div>
  );

  return (
    <div className="relative" ref={rootRef}>
      <Button
        className="flex flex-row items-center gap-2"
        color={values.length ? "primary50" : "gray100"}
        onClick={() => setOpen((o) => !o)}
        rounded="full"
        size="sm"
      >
        {displayText}
        <FiChevronDown
          className={twMerge(
            "shrink-0 transition-transform",
            open && "rotate-180",
          )}
        />
      </Button>

      <AnimatePresence>
        {open && (
          <motion.div
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="absolute z-20 mt-1 w-[260px]"
            exit={{ opacity: 0, scale: 0.98, y: -4 }}
            initial={{ opacity: 0, scale: 0.98, y: -4 }}
            transition={{ duration: 0.12, ease: "easeOut" }}
          >
            <div className="border-gray200 max-h-[28rem] w-full overflow-hidden rounded-md border bg-white shadow-lg">
              {header && (
                <>
                  <div className="px-4 py-3 text-base font-semibold text-black">
                    {header}
                  </div>
                  <Divider color="gray200" type="horizontal" />
                </>
              )}
              {/* Search */}
              <div className="px-4 py-3">
                <input
                  className={twMerge(
                    "border-gray300 h-9 w-full rounded-md border px-4 text-sm outline-none",
                    "focus:border-primary300 focus:shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05),0px_0px_0px_4px_#F4EBFF]",
                  )}
                  name="search"
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Search"
                  value={searchValue}
                />
              </div>

              <Divider color="gray200" type="horizontal" />

              {/* Results */}
              <div
                className="max-h-80 overflow-y-auto"
                id={`${id}-listbox`}
                ref={listRef}
                role="listbox"
                tabIndex={-1}
              >
                {loading ? (
                  <div className="flex items-center justify-center py-4">
                    <Spinner color="primary500" size="sm" />
                  </div>
                ) : !selectedList.length && !unselectedList.length ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="text-gray700 text-sm">No results</div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-1 px-1.5 py-1">
                    {/* Selected first */}
                    {selectedList.map((el) => renderOption(el, true))}
                    {/* Then unselected */}
                    {unselectedList.map((el) => renderOption(el, false))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
